import { useQuery } from "@apollo/client";
import { LOAD_ARTICLE } from "models/queries/article.queries";
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { markdownRenderers } from "../markdown/markdown-renderers";
import "./display-article.component.scss";
import { IAuthenticationState } from "sthinds.io-lib";
import { Link, useParams } from "react-router-dom";
import ArticleAuthorDateBanner from "./article-author-date-banner.component";
import { useSelector } from "react-redux";
import DeleteArticleComponent from "../delete/delete-article.component";

export default function DisplayArticleComponent(): JSX.Element {
  const { articleID } = useParams<{ articleID: string }>();
  const authentication = useSelector(
    (state: { authentication: IAuthenticationState }) => state.authentication
  );

  return <GetFullArticle articleID={articleID} authentication={authentication} />;
}

function GetFullArticle({
  articleID,
  authentication,
}: { articleID?: string; authentication?: IAuthenticationState }): JSX.Element {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { error, loading, data } = useQuery(LOAD_ARTICLE, {
    variables: {
      articleID,
    },
    skip: !articleID,
  });
  const article = data?.article;
  const articleTitle = article?.title;
  const articleDescription = article?.subtitle || "Sean Hinds' technical blog.";

  useEffect(() => {
    if (!articleTitle) return undefined;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content");
    document.title = `${articleTitle} | sthinds.io`;
    description?.setAttribute("content", articleDescription);
    return () => {
      document.title = "sthinds.io";
      if (previousDescription) description?.setAttribute("content", previousDescription);
    };
  }, [articleTitle, articleDescription]);

  if (loading) return null as unknown as JSX.Element;
  if (!articleID) return <p>Article not found.</p>;
  if (error) return `Error! ${error}` as unknown as JSX.Element;
  if (!article) return <p>Article not found.</p>;
  const articleDate = new Date(article.date);
  const articleDateString = articleDate.toLocaleString(); // Convert to string

  return (
    <div className="article-display-div">
      <h1 className="article-title">
        <strong>{article.title}</strong>
      </h1>
      <div className="article-meta flex-row whole-width justify-between">
        <div className="article-subtitle">
          <h5>{article.subtitle}</h5>
        </div>
        <ArticleAuthorDateBanner
          authorID={article.authorID}
          articleDate={articleDateString} // Pass string instead of Date object
        />
      </div>
      <ReactMarkdown
        components={markdownRenderers}
        children={article.text}
      ></ReactMarkdown>
      {/* TODO: hide these when not logged in as author */}
      {authentication?.isLoggedIn &&
      authentication?._id === article.authorID ? (
        <div className="article-actions">
          <Link to={`/article/edit/${article._id}`}>Edit</Link>
          {/* A button, not a Link — it opens a modal rather than navigating,
              and <Link> without `to` does not typecheck. */}
          <button
            type="button"
            className="delete-link"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete
          </button>
        </div>
      ) : (
        ""
      )}

      <DeleteArticleComponent
        article={article}
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
      />
      {/* TODO: add comment and like feature */}
    </div>
  );
}
